import React from 'react';
import { DialogShell } from './ui/DialogShell';
import type { Entorno, Plataforma } from '../utils/detectarPlataforma';

interface InstalarAppDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly entorno: Entorno;
}

interface Instrucciones {
  readonly titulo: string;
  readonly intro?: string;
  readonly pasos: readonly string[];
  readonly nota?: string;
}

const TITULO_INSTALAR = 'Instalar la app';

const INSTRUCCIONES: Record<Plataforma, Instrucciones> = {
  ios: {
    titulo: TITULO_INSTALAR,
    pasos: [
      'Toca el botón Compartir, el cuadrado con la flecha hacia arriba.',
      'Desliza hacia abajo y elige "Agregar a inicio".',
      'Confirma con "Agregar".',
    ],
  },
  'mac-safari': {
    titulo: TITULO_INSTALAR,
    pasos: [
      'En la barra de menús, abre "Archivo".',
      'Elige "Añadir al Dock".',
      'Confirma con "Añadir".',
    ],
  },
  android: {
    titulo: TITULO_INSTALAR,
    pasos: [
      'Abre el menú del navegador, los tres puntos.',
      'Elige "Instalar aplicación" o "Añadir a pantalla principal".',
    ],
  },
  'firefox-windows': {
    titulo: TITULO_INSTALAR,
    pasos: [
      'En la barra de direcciones, toca el ícono "Añadir pestaña a la barra de tareas" (junto a la estrella de marcadores).',
      'Tócalo y confirma "Sí" cuando Windows pregunte si quieres anclarla a la barra de tareas.',
      'Queda un ícono en tu barra de tareas que abre la app en su propia ventana, sin pasar por el navegador.',
    ],
  },
  'firefox-escritorio': {
    titulo: 'Deja la app en tu escritorio',
    intro:
      'Firefox no permite instalar aplicaciones web, pero puedes dejar un acceso directo que la abre con doble clic, sin pasar por el navegador.',
    pasos: [
      'Achica la ventana del navegador para ver tu escritorio detrás.',
      'Arrastra el ícono que está a la izquierda de la dirección (candado, escudo u otro ícono de seguridad, según la página) hasta el escritorio, o hasta la carpeta que prefieras.',
      'Suéltalo: ahí queda el acceso directo.',
    ],
    nota: '¿Prefieres tenerla en tus marcadores o favoritos? Presiona Ctrl+D (Cmd+D en Mac). El acceso directo abre en el navegador predeterminado de tu equipo, no necesariamente en Firefox.',
  },
  generico: {
    titulo: TITULO_INSTALAR,
    pasos: [
      'Abre el menú de tu navegador.',
      'Busca la opción "Instalar", o el ícono de instalar en la barra de direcciones.',
    ],
  },
};

export const InstalarAppDialog: React.FC<InstalarAppDialogProps> = ({
  isOpen,
  onClose,
  entorno,
}) => {
  const { titulo, intro, pasos, nota } = INSTRUCCIONES[entorno.plataforma];

  return (
    <DialogShell isOpen={isOpen} onClose={onClose} title={titulo} size="sm">
      <div className="space-y-4">
        {intro && <p className="text-sm text-slate-600">{intro}</p>}

        <ol className="space-y-3">
          {pasos.map((paso, indice) => (
            <li key={paso} className="flex gap-3 text-sm text-slate-700">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold flex items-center justify-center">
                {indice + 1}
              </span>
              <span className="pt-0.5">{paso}</span>
            </li>
          ))}
        </ol>

        {nota && <p className="text-sm text-slate-500">{nota}</p>}

        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 h-9 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 active:scale-[0.98] transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </DialogShell>
  );
};
